import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_ID_KEY } from "../../Util/VMConstants";
import { API_GET_ENQUIRIES_REPORT } from "../../Util/URLConstants";
import MUITypography from "../../Components/MUITypography";

ChartJS.register(ArcElement, Tooltip, Legend);

const fetchMonthReport = async (userId, month, year) => {
  try {
    const response = await fetch(
      `${API_GET_ENQUIRIES_REPORT}?user_id=${userId}&month=${month}&year=${year}`
    );
    const result = await response.json();
    return result?.data || { lead: 0, confirmed: 0, not_interested: 0 };
  } catch {
    return { lead: 0, confirmed: 0, not_interested: 0 };
  }
};

const CustomerSummaryCard = ({ selectedMonth, selectedYear }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  // const theme = useTheme();
  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  useEffect(() => {
    if (!userID) return;
    setLoading(true);
    fetchMonthReport(userID, selectedMonth, selectedYear)
      .then(setReportData)
      .finally(() => setLoading(false));
  }, [userID, selectedMonth, selectedYear]);

  const { lead = 0, confirmed = 0, not_interested = 0 } = reportData || {};
  const total = lead + confirmed + not_interested;

  const doughnutData = {
    labels: ["Lead", "Confirmed", "Not Interested"],
    datasets: [
      {
        data: [lead, confirmed, not_interested],
        backgroundColor: ["#CC7000", "#0C9320", "#FF0000"],
        borderColor: ["white", "white", "white"],
        borderWidth: 1,
      },
    ],
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "10px",
        background: "#FFFFFF",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header */}
      <MUITypography
      variant="h4"
        sx={{
          fontFamily: "Lato",
          fontWeight: 700,
          fontSize: "18px",
          color: "#000000",
          marginBottom: 2,
        }}
      >
        Customer Summary
      </MUITypography>

      {/* Content */}
      <Box sx={{ display: "flex", gap: "47px" }}>
        {/* Doughnut Chart + Total */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "24px",
          }}
        >
          {/* Total Customers Box */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "10px",
              gap: "19px",
              borderRadius: "6px",
              background: "#1E3A8A",
              width: "100%",
            }}
          >
            <Typography sx={{ color: "#FFFFFF", flex: 1 }}>
              Total Enquired Customers Till date
            </Typography>
            <Typography
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: "20px",
              }}
            >
              {total}
            </Typography>
          </Box>

          {/* Doughnut Chart */}
          {loading ? (
            <CircularProgress />
          ) : (
            <Doughnut
              data={doughnutData}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: false },
                  tooltip: { displayColors: false },
                },
              }}
              style={{ width: "200px", minHeight:'200px'}}
            />
          )}

          {/* Legend */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {[
              { label: "Leads", color: "#CC7000" },
              { label: "Not Interested", color: "#FF0000" },
              { label: "Confirm", color: "#0C9320" },
            ].map(({ label, color }) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: color,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontFamily: "Lato",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Stat Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: 200,
          }}
        >
          {[
            {
              title: "Leads:",
              count: lead,
              bg: "#FFF7ED",
              color: "#CC7000",
            },
            {
              title: "Not Interested:",
              count: not_interested,
              bg: "#FFECEC",
              color: "#FF0000",
            },
            {
              title: "Confirmed:",
              count: confirmed,
              bg: "#E6FFEA",
              color: "#0C9320",
            },
          ].map((item) => (
            <Box
              key={item.title}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "10px",
                gap: "8px",
                borderRadius: "4px",
                background: item.bg,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  flexGrow: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Lato",
                    color: "#6C6C6C",
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    fontFamily: "Lato",
                    color: "#000000",
                  }}
                >
                  {item.count}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default CustomerSummaryCard;
